import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import styled from '@emotion/styled';
import {
  StyledFormGroup,
  StyledInput,
  StyledButton,
  StyledCheckboxFormControl,
  GoogleButton,
} from '@selfserve/react-ui';
import { buttonIds, routesPaths, socialType, responseTypes, getRecaptchaToken } from '@selfserve/react-utils';
import { GoogleButtonText, GoogleOAuthResult, useLog, useGoogleOAuth, useQueryParams } from '@selfserve/react-hooks';
import { FormValues } from '../feature/login';
import { useNavigate } from 'react-router-dom';
import { useSocialLoginRequest } from '../data';
import { Box } from '@kep-ui-kit/ui-components';

const StyledCheckboxGroup = styled(StyledFormGroup)`
  flex-direction: row;
  justify-content: space-between;
`;

const SecondaryButton = styled(StyledButton)`
  background-color: #ebebeb;
  color: #333;
  border: none;

  &:hover {
    background-color: #d4d4d4;
  }

  &:disabled {
    background-color: #ebebeb;
    color: #999;
  }
`;

const DividerWithText = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: #d4d4d4;
  }
`;

const DividerText = styled.span`
  color: #333;
  font-size: 16px;
  font-family: 'Lato', sans-serif;
  font-weight: 400;
`;

interface Props {
  formik: FormikProps<FormValues>;
  handleTogglePasswordlessMode: () => void;
  isLoading: boolean;
  passwordlessMode: boolean;
  openErrorModal: (options?: any) => void;
}

export const LoginForm = ({
  formik: { handleSubmit, handleChange, handleBlur, values, errors, touched },
  handleTogglePasswordlessMode,
  passwordlessMode,
  isLoading,
  openErrorModal,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { log } = useLog();
  const queryParams = useQueryParams();
  const { mutateAsync: handleSocialLoginRequest, isLoading: isSocialLoginLoading } = useSocialLoginRequest();

  const handleGoogleSuccess = useCallback(
    (result: GoogleOAuthResult) => {
      getRecaptchaToken()
        .then((recaptchaToken: string) =>
          handleSocialLoginRequest({
            type: socialType.GOOGLE,
            data: { token: result.token },
            recaptchaToken,
            ...(queryParams['productFamily'] && { productFamily: queryParams['productFamily'] })
          })
        )
        .then(({ status, data }) => {
          if (String(status).startsWith(responseTypes.SUCCESS)) {
            if (data?.url) {
              window.location.replace(data.url);
            } else {
              navigate(routesPaths.MANAGEMENT_OVERVIEW);
            }
          } else if (data?.message_en) {
            openErrorModal({ text: data.message_en });
          } else {
            openErrorModal({ text: t(`oauth.${socialType.GOOGLE}-error`) });
          }
        })
        .catch((error: Error) => {
          log(error);
          openErrorModal({ text: t(`oauth.${socialType.GOOGLE}-error`) });
        });
    },
    [handleSocialLoginRequest, openErrorModal, t, log]
  );

  const handleGoogleError = useCallback(
    (error: Error) => {
      log(error);
      openErrorModal({ text: t(`oauth.${socialType.GOOGLE}-error`) });
    },
    [log, openErrorModal, t]
  );

  const { googleButtonRef } = useGoogleOAuth({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
    text: GoogleButtonText.SIGN_IN_WITH,
  });

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <StyledFormGroup withoutMargin>
          <StyledInput
            id="txtEmail"
            name="email"
            inputLabel={t('common.email')}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email}
            error={touched.email ? !!errors.email : false}
            inputProps={{ 'aria-label': t('aria.email') }}
            autoFocus
            fullWidth
          />
          {touched.email && errors.email && (
            <p className="p-error">{errors.email}</p>
          )}
        </StyledFormGroup>
        {!passwordlessMode && (
          <StyledFormGroup withoutMargin>
            <StyledInput
              id="txtPass"
              name="password"
              type="password"
              inputLabel={t('common.password')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.password}
              error={touched.password ? !!errors.password : false}
              inputProps={{ 'aria-label': t('aria.password') }}
              fullWidth
            />
            {touched.password && errors.password && (
              <p className="p-error">{errors.password}</p>
            )}
          </StyledFormGroup>
        )}
        {!passwordlessMode && (
          <StyledCheckboxGroup isFormControl withoutMargin>
            <StyledCheckboxFormControl
              label={t('common.remember-me')}
              name="rememberMe"
              onClick={handleChange}
              value={values.rememberMe}
            />
            <StyledButton
              onClick={handleTogglePasswordlessMode}
              id={buttonIds.loginWithoutPassword}
              type="button"
              disabled={isLoading}
              isLink
              style={{ marginBottom: 0 }}
            >
              {t('common.login-without-password')}
            </StyledButton>
          </StyledCheckboxGroup>
        )}
        <StyledButton
          id={buttonIds.login}
          type="submit"
          loading={isLoading}
          fullWidth
          style={{ marginBottom: 0 }}
        >
          {passwordlessMode ? t('common.continue-with-email') : t('common.login')}
        </StyledButton>
      </Box>
      <DividerWithText>
        <DividerText>{t('common.or')}</DividerText>
      </DividerWithText>
      {passwordlessMode && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <SecondaryButton
            onClick={() => navigate(routesPaths.FORGOT_PASSWORD)}
            type="button"
            disabled={isLoading}
            fullWidth
            withoutMargin
          >
            {t('common.reset-password')}
          </SecondaryButton>
          <SecondaryButton
            onClick={handleTogglePasswordlessMode}
            type="button"
            disabled={isLoading}
            fullWidth
            withoutMargin
          >
            {t('common.login-with-password')}
          </SecondaryButton>
        </Box>
      )}
      {!passwordlessMode && (
        <GoogleButton
          buttonRef={googleButtonRef}
          id={buttonIds.googleLogin}
          disabled={isLoading || isSocialLoginLoading}
        />
      )}
    </form>
  );
};
